// @ts-nocheck

/**
 * Standard paths for image uploads to ensure consistency
 */
export const UPLOAD_PATHS = {
  AUCTIONS: "auctions/",
  ARTWORKS: "artworks/",
  LOTS: "lots/",
  PROFILES: "profiles/", 
  CONSIGNMENTS: "consignments/"
};

/**
 * Default placeholder images for different content types
 */
export const DEFAULT_IMAGES = {
  ARTWORK: "/placeholder.svg",
  AUCTION: "/placeholder.svg",
  PROFILE: "/placeholder.svg",
  GENERIC: "/placeholder.svg"
};

/**
 * Normalize image URLs by simply trimming whitespace. Do not alter paths.
 */
export const normalizeImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return url as any;
  return url.trim();
};

/**
 * Validates if a URL string represents a valid image URL
 * @param url The URL to validate
 * @returns boolean indicating if the URL is valid
 */
export const isValidImageUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  
  // Handle empty strings
  if (url.trim() === '') return false;
  
  // Relative path starting with "/" is acceptable
  if (url.startsWith('/')) {
    return url.length > 1;
  }
  
  // For absolute URLs, validate with URL constructor
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Convert Supabase public object URLs to render/image URLs with transformation params
 * Falls back to original URL if pattern does not match
 */
export const toSupabaseRenderUrl = (
  url: string | null | undefined,
  options: { width?: number; height?: number; quality?: number; resize?: 'cover' | 'contain' | 'fill' } = {}
): string | null => {
  if (!url) return url as any;
  const trimmed = url.trim();
  try {
    const u = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : 'https://floridahighwaymenauction.com');
    // Match /storage/v1/object/public/<bucket>/<path>
    const objectPathIndex = u.pathname.indexOf('/storage/v1/object/public/');
    if (objectPathIndex === -1) return trimmed; // Not a Supabase public storage URL
    const publicPath = u.pathname.substring(objectPathIndex + '/storage/v1/object/public/'.length);
    const base = `${u.origin}/storage/v1/render/image/public/${publicPath}`;
    const params = new URLSearchParams();
    if (options.width) params.set('width', String(options.width));
    if (options.height) params.set('height', String(options.height));
    if (options.quality) params.set('quality', String(options.quality));
    if (options.resize) params.set('resize', options.resize);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  } catch {
    return trimmed;
  }
};

/**
 * Gets a safe image URL, returning a default if the provided URL is invalid
 * @param url The image URL to validate
 * @param fallbackImage The fallback image to use if invalid
 * @returns A safe image URL
 */
export const getSafeImageUrl = (
  url: string | null | undefined,
  fallbackImage: string = DEFAULT_IMAGES.GENERIC
): string => {
  const normalized = normalizeImageUrl(url || '') || '';
  if (isValidImageUrl(normalized)) {
    return normalized as string;
  }
  return fallbackImage;
};

/**
 * Standardizes a file path for upload to ensure consistent storage structure
 * @param fileName The filename to use
 * @param pathPrefix The path prefix to use (from UPLOAD_PATHS)
 * @returns The standardized file path
 */
export const getStandardizedFilePath = (
  fileName: string,
  pathPrefix: string = UPLOAD_PATHS.AUCTIONS
): string => {
  // Remove any leading slashes from the path prefix
  const normalizedPrefix = pathPrefix.replace(/^\/+/, '');
  
  // Ensure path ends with a slash
  const prefixWithSlash = normalizedPrefix.endsWith('/') 
    ? normalizedPrefix 
    : `${normalizedPrefix}/`;
  
  // Remove any path information from the filename to prevent path traversal
  const safeFileName = fileName.split('/').pop() || fileName;
  
  return `${prefixWithSlash}${safeFileName}`;
};

/**
 * Handles image errors and provides fallback
 * @param event The error event
 * @param fallbackSrc The fallback image source
 * @param logDetails Optional details to log with the error
 */
export const handleImageError = (
  event: React.SyntheticEvent<HTMLImageElement, Event>,
  fallbackSrc: string = DEFAULT_IMAGES.GENERIC,
  logDetails?: string
): void => {
  const target = event.currentTarget;
  console.error(`Image failed to load: ${target.src}${logDetails ? ` (${logDetails})` : ''}`);
  
  // Only set fallback if it's different to prevent infinite error loops
  if (target.src !== fallbackSrc) {
    target.src = fallbackSrc;
  }
};

/**
 * Safe image component props
 */
export interface SafeImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string | null | undefined;
  alt: string;
  fallbackSrc?: string;
  logErrorDetails?: string;
  className?: string;
}

/**
 * Checks if an image URL is valid and accessible
 * @param url The URL to check
 * @returns Promise resolving to boolean indicating if the image is accessible
 */
export const isImageAccessible = (url: string | null | undefined): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }
    
    const img = new Image();
    
    img.onload = () => {
      resolve(true);
    };
    
    img.onerror = () => {
      resolve(false);
    };
    
    // Use our existing utility to get a safe URL
    img.src = getSafeImageUrl(url);
  });
};

/**
 * Preload an image to ensure it's in the browser cache
 * @param url The image URL to preload
 * @returns Promise resolving when the image is loaded
 */
export const preloadImage = (url: string | null | undefined): Promise<void> => {
  if (!url) return Promise.resolve();
  
  return new Promise((resolve, reject) => {
    const safeUrl = getSafeImageUrl(url);
    const img = new Image();
    
    img.onload = () => {
      resolve();
    };
    
    img.onerror = (e) => {
      reject(new Error(`Failed to preload image: ${safeUrl}`));
    };
    
    img.src = safeUrl;
  });
};

/**
 * Get image dimensions from a URL
 * @param url The image URL
 * @returns Promise resolving to the image dimensions {width, height}
 */
export const getImageDimensions = (url: string | null | undefined): Promise<{width: number, height: number} | null> => {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }
    
    const safeUrl = getSafeImageUrl(url);
    const img = new Image();
    
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height
      });
    };
    
    img.onerror = () => {
      resolve(null);
    };
    
    img.src = safeUrl;
  });
};

/**
 * Track image load and error events for analytics and debugging
 * @param event The load or error event
 * @param url The image URL
 * @param context Additional context information
 */
export const trackImageEvent = (
  event: 'load' | 'error',
  url: string | null | undefined,
  context?: string
): void => {
  if (event === 'load') {
    console.log(`Image loaded successfully${context ? ` (${context})` : ''}: ${url}`);
  } else {
    console.error(`Image failed to load${context ? ` (${context})` : ''}: ${url}`);
  }
  
  // Could expand with actual analytics tracking here
};
